import { useState, type ReactElement } from 'react';
import { ActionIcon, Box, Button, Checkbox, Collapse, Divider, Group, Indicator, NumberInput, Popover, Select, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { Calendar, ChevronDown, ChevronRight, Filter, Hash, Info, List, Lock, Percent, ToggleLeft, Type, X } from 'lucide-react';
import type { MegaGridColumn, MegaGridExternalFilter, MegaGridFilterInstance, MegaGridFilterOp, MegaGridFilterType } from './MegaGridTypes';
import { defaultOpForType, opArity, opLabel, opSelectData } from './MegaGridFilterMeta';

/** Icon hinting at a column's data type, shown in the Add-filter menu. */
function typeIcon(type: MegaGridFilterType): ReactElement {
    switch (type) {
        case 'number':
            return <Hash size={14} />;
        case 'percent':
            return <Percent size={14} />;
        case 'date':
            return <Calendar size={14} />;
        case 'enum':
            return <List size={14} />;
        case 'boolean':
            return <ToggleLeft size={14} />;
        default:
            return <Type size={14} />;
    }
}

function valueLabel(f: MegaGridFilterInstance, type: MegaGridFilterType, enumOptions: { label: string; value: string }[]): string {
    const arity = opArity(f.op);
    if (arity === 'none') return '';
    if (arity === 'multi') {
        const labels = (f.values ?? []).map((v) => enumOptions.find((o) => o.value === v)?.label ?? v);
        if (labels.length === 0) return '…';
        if (labels.length <= 2) return labels.join(', ');
        return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
    }
    const unit = type === 'percent' ? '%' : '';
    if (arity === 'double') return `${f.value ?? '…'}${unit} – ${f.value2 ?? '…'}${unit}`;
    return f.value === undefined || f.value === null || f.value === '' ? '…' : `${f.value}${unit}`;
}

function isIncomplete(f: MegaGridFilterInstance): boolean {
    const arity = opArity(f.op);
    if (arity === 'none') return false;
    if (arity === 'multi') return (f.values ?? []).length === 0;
    if (arity === 'double') return (f.value == null || f.value === '') && (f.value2 == null || f.value2 === '');
    return f.value == null || f.value === '';
}

function FilterValueEditor<T>({
    f,
    column,
    enumOptions,
    onUpdate,
}: {
    f: MegaGridFilterInstance;
    column: MegaGridColumn<T>;
    enumOptions: { label: string; value: string }[];
    onUpdate: (id: string, patch: Partial<MegaGridFilterInstance>) => void;
}): ReactElement | null {
    const arity = opArity(f.op);
    const type = column.filter!.type;
    if (arity === 'none') return null;

    if (type === 'enum') {
        return (
            <Stack gap={4} mah={220} style={{ overflowY: 'auto' }}>
                {enumOptions.length === 0 && (
                    <Text size="xs" c="dimmed">
                        No values
                    </Text>
                )}
                {enumOptions.map((opt) => {
                    const selected = f.values ?? [];
                    const checked = selected.includes(opt.value);
                    return (
                        <Checkbox
                            key={opt.value}
                            size="xs"
                            label={opt.label}
                            checked={checked}
                            onChange={() => onUpdate(f.id, { values: checked ? selected.filter((v) => v !== opt.value) : [...selected, opt.value] })}
                        />
                    );
                })}
            </Stack>
        );
    }

    if (type === 'number' || type === 'percent') {
        const suffix = type === 'percent' ? '%' : undefined;
        return (
            <Group gap="xs" grow>
                <NumberInput size="xs" suffix={suffix} placeholder={arity === 'double' ? 'From' : 'Value'} value={f.value ?? undefined} onChange={(v) => onUpdate(f.id, { value: v === '' ? null : Number(v) })} />
                {arity === 'double' && <NumberInput size="xs" suffix={suffix} placeholder="To" value={f.value2 ?? undefined} onChange={(v) => onUpdate(f.id, { value2: v === '' ? null : Number(v) })} />}
            </Group>
        );
    }

    if (type === 'date') {
        return (
            <Group gap="xs" grow>
                <TextInput type="date" size="xs" value={(f.value as string) ?? ''} onChange={(e) => onUpdate(f.id, { value: e.currentTarget.value || null })} />
                {arity === 'double' && <TextInput type="date" size="xs" value={(f.value2 as string) ?? ''} onChange={(e) => onUpdate(f.id, { value2: e.currentTarget.value || null })} />}
            </Group>
        );
    }

    return <TextInput autoFocus size="xs" placeholder="Value" value={(f.value as string) ?? ''} onChange={(e) => onUpdate(f.id, { value: e.currentTarget.value })} />;
}

interface MegaGridFilterControlsProps<T> {
    columns: MegaGridColumn<T>[];
    filters: MegaGridFilterInstance[];
    externalFilters: MegaGridExternalFilter[];
    enumOptions: Record<string, { label: string; value: string }[]>;
    onAdd: (columnKey: string, op: MegaGridFilterOp) => void;
    onUpdate: (id: string, patch: Partial<MegaGridFilterInstance>) => void;
    onRemove: (id: string) => void;
    onClear: () => void;
}

/**
 * The toolbar's filter control: a single funnel icon button (an {@link Indicator}
 * badge shows the active-filter count) that opens one popover holding both the
 * applied filters — the host's global filters first (read-only, tooltip'd) then
 * the grid's own filters with inline editors — and an "Add filter" section listing
 * the filterable columns. Adding a filter keeps the popover open and expands the
 * new filter's editor inline.
 */
export default function MegaGridFilterControls<T>({ columns, filters, externalFilters, enumOptions, onAdd, onUpdate, onRemove, onClear }: MegaGridFilterControlsProps<T>): ReactElement | null {
    const [opened, setOpened] = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);

    const filterable = columns.filter((c) => c.filter);
    if (filterable.length === 0 && externalFilters.length === 0) return null;
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const count = filters.length + externalFilters.length;
    const expandedId = expanded === 'latest' ? (filters[filters.length - 1]?.id ?? null) : expanded;

    const handleAdd = (columnKey: string, op: MegaGridFilterOp): void => {
        onAdd(columnKey, op);
        setExpanded('latest');
        setOpened(true);
    };

    return (
        <Group gap={6} wrap="nowrap">
            {(count > 0 || filterable.length > 0) && (
                <Popover opened={opened} onChange={setOpened} position="bottom-start" width={320} shadow="md" radius="md" withinPortal>
                    <Popover.Target>
                        <Indicator label={count} disabled={count === 0} size={16} color="brand" offset={4} withBorder>
                            <Tooltip label="Filters" withArrow>
                                <ActionIcon variant="default" aria-label="Filters" onClick={() => setOpened((current) => !current)}>
                                    <Filter size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Indicator>
                    </Popover.Target>
                    <Popover.Dropdown>
                        <Stack gap="sm">
                            {externalFilters.length > 0 && (
                                <Box>
                                    <Group gap={6} mb={6} wrap="nowrap">
                                        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                                            Global filters
                                        </Text>
                                        <Tooltip label="Set in the top nav — they always apply to this grid and can only be changed there." withArrow multiline w={240}>
                                            <Info size={12} style={{ color: 'var(--bm-color-text-dimmed)', display: 'block' }} />
                                        </Tooltip>
                                    </Group>
                                    <Stack gap={4}>
                                        {externalFilters.map((external) => (
                                            <Tooltip key={external.id} label="Global filter — change it in the top nav" withArrow position="left">
                                                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                                                    <Lock size={11} style={{ color: 'var(--bm-color-text-dimmed)', flexShrink: 0 }} />
                                                    <Text size="xs" fw={600} style={{ flexShrink: 0 }}>
                                                        {external.label}:
                                                    </Text>
                                                    <Text size="xs" c="dimmed" truncate>
                                                        {external.value}
                                                    </Text>
                                                </Group>
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            {externalFilters.length > 0 && filters.length > 0 && <Divider />}
                            {filters.length > 0 && (
                                <Box>
                                    {externalFilters.length > 0 && (
                                        <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.06em' }}>
                                            Grid filters
                                        </Text>
                                    )}
                                    <Stack gap={4}>
                                        {filters.map((f) => {
                                            const column = byKey.get(f.columnKey);
                                            if (!column?.filter) return null;
                                            const isOpen = expandedId === f.id;
                                            const incomplete = isIncomplete(f);
                                            return (
                                                <Box key={f.id}>
                                                    <Group gap={4} wrap="nowrap" justify="space-between">
                                                        <UnstyledButton onClick={() => setExpanded(isOpen ? null : f.id)} style={{ minWidth: 0, flex: 1 }}>
                                                            <Group gap={6} wrap="nowrap">
                                                                {isOpen ? <ChevronDown size={12} style={{ flexShrink: 0 }} /> : <ChevronRight size={12} style={{ flexShrink: 0 }} />}
                                                                <Text size="xs" truncate style={incomplete ? { color: 'var(--bm-color-caution-text, var(--mantine-color-yellow-8))' } : undefined}>
                                                                    <Text span fw={600} inherit>
                                                                        {column.header}
                                                                    </Text>{' '}
                                                                    <Text span c="dimmed" inherit>
                                                                        {opLabel(f.op)}
                                                                    </Text>{' '}
                                                                    <Text span fw={500} inherit>
                                                                        {valueLabel(f, column.filter.type, enumOptions[f.columnKey] ?? [])}
                                                                    </Text>
                                                                </Text>
                                                            </Group>
                                                        </UnstyledButton>
                                                        <ActionIcon
                                                            size="xs"
                                                            variant="subtle"
                                                            color="gray"
                                                            onClick={() => {
                                                                onRemove(f.id);
                                                                if (isOpen) setExpanded(null);
                                                            }}
                                                            aria-label={`Remove ${column.header} filter`}
                                                        >
                                                            <X size={12} />
                                                        </ActionIcon>
                                                    </Group>
                                                    <Collapse in={isOpen}>
                                                        {isOpen && (
                                                            <Stack gap="xs" pl={18} pt={6} pb={4}>
                                                                <Select
                                                                    size="xs"
                                                                    data={opSelectData(column.filter.type)}
                                                                    value={f.op}
                                                                    allowDeselect={false}
                                                                    comboboxProps={{ withinPortal: false }}
                                                                    onChange={(op) => op !== null && onUpdate(f.id, { op: op as MegaGridFilterInstance['op'] })}
                                                                    aria-label="Operator"
                                                                />
                                                                <FilterValueEditor f={f} column={column} enumOptions={enumOptions[f.columnKey] ?? []} onUpdate={onUpdate} />
                                                            </Stack>
                                                        )}
                                                    </Collapse>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                    <Button
                                        size="compact-xs"
                                        variant="subtle"
                                        color="gray"
                                        mt="xs"
                                        onClick={() => {
                                            onClear();
                                            setExpanded(null);
                                        }}
                                    >
                                        Clear grid filters
                                    </Button>
                                </Box>
                            )}
                            {filterable.length > 0 && (
                                <Box>
                                    {count > 0 && <Divider mb="sm" />}
                                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6} style={{ letterSpacing: '0.06em' }}>
                                        Add filter
                                    </Text>
                                    <Stack gap={2} mah={240} style={{ overflowY: 'auto' }}>
                                        {filterable.map((c) => (
                                            <Button
                                                key={c.key}
                                                variant="subtle"
                                                color="gray"
                                                size="compact-sm"
                                                fullWidth
                                                justify="flex-start"
                                                leftSection={typeIcon(c.filter!.type)}
                                                styles={{ label: { fontWeight: 400 } }}
                                                onClick={() => handleAdd(c.key, defaultOpForType(c.filter!.type))}
                                            >
                                                {c.filter!.label ?? c.header}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    </Popover.Dropdown>
                </Popover>
            )}
        </Group>
    );
}
